import { AxiosError } from 'axios';
import { useCallback } from 'react';

import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import { SimpleErrorResponse } from '@/models/Error';
import { queryClient } from '@/utils/query';

import { addonTrialQueryKey, TrialAddonType } from './useAddonTrialShow';

type Callbacks = { onSuccess?: () => void; onError?: (error: AxiosError<SimpleErrorResponse>) => void };
export enum BuildCacheTrialSource {
  buildDetailsPageBuildCacheTab = 'build_details_page_build_cache_tab',
  bitriseCiSetupFlow = 'bitrise_ci_setup_flow',
  nonBitriseCiSetupFlow = 'non_bitrise_ci_setup_flow',
  localSetupFlow = 'local_setup_flow',
  billingPage = 'billing_page',
}

// Starts a Build Cache trial via the subscription-service add-on trial system, replacing the legacy
// metadata endpoint (POST /workspaces/:slug/build_cache_trial). Invalidates the trial-status query on
// success so cards/banners re-derive their state without a full page reload.
export const useBuildCacheStartTrial = (workspaceSlug: string, { onSuccess, onError }: Callbacks = {}) => {
  const { mutate, isPending, isError, error, reset } = useMonolithApiMutation<
    void,
    SimpleErrorResponse,
    { type: TrialAddonType; source: BuildCacheTrialSource }
  >(`/organizations/${workspaceSlug}/addon_trials`, 'POST', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: addonTrialQueryKey(workspaceSlug, TrialAddonType.BuildCache) });
      onSuccess?.();
    },
    onError: (error) => onError?.(error),
  });

  // Stable reference so effect-driven callers (e.g. ConfigureWorkflowStep's auto-start) don't re-run
  // on every render; mutate from react-query is itself stable.
  const startTrial = useCallback(
    (source: BuildCacheTrialSource) => mutate({ type: TrialAddonType.BuildCache, source }),
    [mutate],
  );

  return { startTrial, isLoading: isPending, isError, error, reset };
};
